import { Step } from '../Stepper'


export type StageId =
  | 'imports'
  | 'drivers'
  | 'activity'
  | 'violations'
  | 'delegations'
  | 'evidence.choose'
  | 'evidence.details'
  | 'summary.printouts'
  | 'summary.review'


export type CreatorGroupId =
  | 'imports'
  | 'drivers'
  | 'activity'
  | 'violations'
  | 'delegations'
  | 'evidence'
  | 'summary'

export type CreatorStage = {
  id: StageId
  title: string
  groupId: CreatorGroupId
}

export type CreatorGroup = {
  id: CreatorGroupId
  title: string
  stageIds: StageId[]
}

export const creatorGroups: CreatorGroup[] = [
  { id: 'imports', title: 'Import kierowców i danych', stageIds: ['imports'] },
  { id: 'drivers', title: 'Wskaż kierowców do rozliczenia', stageIds: ['drivers'] },
  { id: 'activity', title: 'Wprowadzanie nieobecności', stageIds: ['activity'] },
  { id: 'violations', title: 'Sprawdź naruszenia', stageIds: ['violations'] },
  { id: 'delegations', title: 'Delegacje', stageIds: ['delegations'] },
  {
    id: 'evidence',
    title: 'Ewidencja',
    stageIds: ['evidence.choose', 'evidence.details'],
  },
  {
    id: 'summary',
    title: 'Podsumowanie',
    stageIds: ['summary.printouts', 'summary.review'],
  },
]

const STAGE_TITLES: Record<StageId, string> = {
  imports: 'Import kierowców i danych',
  drivers: 'Wskaż kierowców do rozliczenia',
  activity: 'Wprowadzanie nieobecności',
  violations: 'Sprawdź naruszenia',
  delegations: 'Delegacje',
  'evidence.choose': 'Wygeneruj ewidencję czasu pracy wszystkich kierowców',
  'evidence.details': 'Ewidencja - szczegóły',
  'summary.printouts': 'Wybierz i generuj wydruki',
  'summary.review': 'Sprawdź i zaakceptuj',
}

export const creatorStages: CreatorStage[] = creatorGroups.flatMap((group) =>
  group.stageIds.map<CreatorStage>((id) => ({
    id,
    title: STAGE_TITLES[id],
    groupId: group.id,
  })),
)

export const stageIndexOf = (id: StageId): number =>
  creatorStages.findIndex((stage) => stage.id === id)

export const groupOfStage = (id: StageId): CreatorGroup => {
  const group = creatorGroups.find((g) => g.stageIds.includes(id))
  if (!group) {
    throw new Error(`Stage ${id} does not belong to any creator group`)
  }
  return group
}


const LEGACY_STEP_BY_GROUP: Record<CreatorGroupId, number> = {
  imports: 0,
  drivers: 1,
  activity: 2,
  violations: 3,
  delegations: 4,
  evidence: 5,
  summary: 6,
}

export const stageToLegacy = (
  id: StageId,
): { step: number; subStep: number } => {
  const group = groupOfStage(id)
  const step = LEGACY_STEP_BY_GROUP[group.id]
  const subStep =
    group.stageIds.length > 1 ? group.stageIds.indexOf(id) + 1 : 0
  return { step, subStep }
}

export const legacyToStage = (step: number, subStep: number): StageId => {
  const group = creatorGroups.find(
    (g) => LEGACY_STEP_BY_GROUP[g.id] === step,
  )
  if (!group) return 'drivers'
  if (group.stageIds.length === 1) return group.stageIds[0]
  const idx = Math.max(0, subStep - 1)
  return group.stageIds[idx] ?? group.stageIds[0]
}


export const creatorSteps: Step[] = creatorGroups.map((group) => ({
  id: group.id,
  title: group.title,
  status: 'upcoming',
  children:
    group.stageIds.length > 1
      ? group.stageIds.map((stageId) => ({
          id: stageId,
          title: STAGE_TITLES[stageId],
          status: 'upcoming',
        }))
      : undefined,
}))
